import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, X } from "lucide-react";

function uid() { return Math.random().toString(36).slice(2, 10); }

export default function RSVPFormBuilder({ questions, setQuestions }) {
  const add = () => setQuestions([...questions, { id: uid(), label: "", type: "text", options: [], required: false }]);
  const update = (i, patch) => setQuestions(questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const remove = (i) => setQuestions(questions.filter((_, idx) => idx !== i));
  const addOpt = (i) => update(i, { options: [...(questions[i].options || []), ""] });
  const setOpt = (i, oi, v) => {
    const opts = [...(questions[i].options || [])]; opts[oi] = v;
    update(i, { options: opts });
  };
  const removeOpt = (i, oi) => {
    const opts = [...(questions[i].options || [])]; opts.splice(oi, 1);
    update(i, { options: opts });
  };

  return (
    <div className="space-y-4">
      {questions.map((q, i) => (
        <div key={q.id} className="border border-black/10 p-5 bg-white">
          <div className="flex items-start gap-3">
            <div className="flex-1 grid gap-3 sm:grid-cols-[1fr_200px]">
              <div>
                <Label className="text-[10px] tracking-[0.25em] uppercase">Question</Label>
                <Input
                  data-testid={`question-label-${i}`}
                  value={q.label}
                  onChange={(e) => update(i, { label: e.target.value })}
                  placeholder="e.g. Song request?"
                  className="rounded-sm mt-1"
                />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.25em] uppercase">Type</Label>
                <Select value={q.type} onValueChange={(v) => update(i, { type: v, options: v === "text" ? [] : (q.options || []) })}>
                  <SelectTrigger data-testid={`question-type-${i}`} className="rounded-sm mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Free text</SelectItem>
                    <SelectItem value="dropdown">Dropdown</SelectItem>
                    <SelectItem value="checkbox">Checkboxes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <button type="button" onClick={() => remove(i)} data-testid={`question-remove-${i}`} className="mt-6 text-neutral-400 hover:text-red-600">
              <Trash2 size={18} />
            </button>
          </div>
          {(q.type === "dropdown" || q.type === "checkbox") && (
            <div className="mt-4 pl-1">
              <Label className="text-[10px] tracking-[0.25em] uppercase">Options</Label>
              <div className="space-y-2 mt-2">
                {(q.options || []).map((opt, oi) => (
                  <div key={oi} className="flex items-center gap-2">
                    <Input
                      data-testid={`question-${i}-option-${oi}`}
                      value={opt}
                      onChange={(e) => setOpt(i, oi, e.target.value)}
                      placeholder={`Option ${oi + 1}`}
                      className="rounded-sm"
                    />
                    <button type="button" onClick={() => removeOpt(i, oi)} className="text-neutral-400 hover:text-red-600">
                      <X size={16} />
                    </button>
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => addOpt(i)} data-testid={`question-${i}-add-option`} className="rounded-sm">
                  <Plus size={14} className="mr-1" /> Add option
                </Button>
              </div>
            </div>
          )}
          <div className="mt-4 flex items-center gap-2">
            <Checkbox
              id={`req-${q.id}`}
              checked={q.required}
              onCheckedChange={(v) => update(i, { required: !!v })}
              data-testid={`question-required-${i}`}
            />
            <label htmlFor={`req-${q.id}`} className="text-sm text-neutral-700">Required</label>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={add} data-testid="add-question-btn" className="rounded-sm">
        <Plus size={16} className="mr-2" /> Add a custom question
      </Button>
    </div>
  );
}
